import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { logWarn } from "../lib/log";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = authed (with .role)
  const [error, setError] = useState("");
  // Panne réseau / serveur endormi : on distingue « session expirée » (401) d'une
  // indisponibilité, pour proposer une nouvelle tentative au lieu d'attendre sans fin.
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { signal: controller.signal });
        setAuthUnavailable(false);
        setUser(data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setAuthUnavailable(false);
          setUser(false);
        } else {
          logWarn("[auth] /auth/me indisponible", e);
          setAuthUnavailable(true);
          setUser(false);
        }
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => { clearTimeout(timer); controller.abort(); };
  }, [attempt]);

  const retryAuth = () => { setUser(null); setAuthUnavailable(false); setAttempt((a) => a + 1); };

  const login = async (email, password) => {
    setError("");
    setAuthUnavailable(false);
    try {
      const { data: res } = await api.post("/auth/login", { email, password });
      // Connexion agent depuis l'étranger : vérification SMS avant toute session
      if (res?.mfa_required) return { mfa_required: true, ...res };
      // Re-hydrate depuis /auth/me pour récupérer tous les champs (prenom, etc.)
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  };

  const completeMfa = async () => {
    const { data } = await api.get("/auth/me");
    setAuthUnavailable(false);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logWarn("[auth] logout call failed", e);
    }
    setAuthUnavailable(false);
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, login, logout, error, completeMfa, authUnavailable, retryAuth }),
    [user, error, authUnavailable],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
